import { useMemo } from 'react';

import { useBankAccount } from '@commenda/api/bankAccount';
import {
  useIndirectTaxFilingV2,
  useIndirectTaxFilingV2Contributions,
  useIndirectTaxFilingV2DocumentsWithFiles,
} from '@commenda/api/indirect-tax/filings';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { useIndirectTaxFilingAccess } from '../../common/hooks/useIndirectTaxFilingAccess';
import {
  IndirectTaxFilingReturnSummaryHelper,
  IndirectTaxFilingsHelper,
} from '../helpers';
import {
  IIndirectTaxFilingDetail,
  IIndirectTaxFilingReturnSummary,
} from '../types';

interface UseIndirectTaxFilingDetailProps {
  filingId: string;
  corporationId: number;
}

interface UseIndirectTaxFilingDetailReturn {
  filing: IIndirectTaxFilingDetail | null;
  region: IndirectTaxRegion;
  returnSummary: IIndirectTaxFilingReturnSummary | null;
  canViewDetails: boolean;
  isLoading: boolean;
  error: unknown;
  mutate: () => void;
}

export function useIndirectTaxFilingDetail({
  filingId,
  corporationId,
}: UseIndirectTaxFilingDetailProps): UseIndirectTaxFilingDetailReturn {
  const {
    filing: rawFiling,
    isLoading: isLoadingFiling,
    error: filingError,
    mutate,
  } = useIndirectTaxFilingV2(filingId, {
    corporationId,
    includeServiceRequest: true,
    includeFiledByAgent: true,
    includeAssignedUser: true,
  });

  const { data: bankAccountsData } = useBankAccount({ corporationId });

  const detectedRegion = useMemo(() => {
    if (!rawFiling) return IndirectTaxRegion.ALL_REGIONS;
    return IndirectTaxRegionHelper.deriveRegionFromJurisdiction({
      jurisdictionType: rawFiling.jurisdictionType,
      jurisdictionName: rawFiling.jurisdictionName,
      country: rawFiling.country,
    });
  }, [rawFiling]);

  const { canViewDetails } = useIndirectTaxFilingAccess(detectedRegion);
  const { confirmationDocuments, paymentDocuments, returnDocuments } =
    useIndirectTaxFilingV2DocumentsWithFiles(
      rawFiling && canViewDetails ? filingId : null,
      corporationId
    );

  const shouldFetchContributions =
    canViewDetails &&
    IndirectTaxRegionHelper.isContributionRegion(detectedRegion) &&
    Boolean(filingId);

  const {
    contributions,
    isLoading: isLoadingContributions,
    error: contributionsError,
  } = useIndirectTaxFilingV2Contributions(
    shouldFetchContributions ? filingId : null,
    shouldFetchContributions ? { corporationId } : null
  );

  const remittanceAccount = useMemo(() => {
    const accounts = bankAccountsData?.accounts ?? [];
    return accounts.find((a) => a.isRemittanceAccount) ?? null;
  }, [bankAccountsData]);

  const returnSummary = useMemo<IIndirectTaxFilingReturnSummary | null>(() => {
    if (!rawFiling || !canViewDetails) return null;
    const currency = IndirectTaxFilingsHelper.getFilingCurrency(
      rawFiling,
      detectedRegion
    );
    if (!IndirectTaxRegionHelper.supportsFilingWorkflow(detectedRegion))
      return null;
    if (IndirectTaxRegionHelper.isContributionRegion(detectedRegion)) {
      return IndirectTaxFilingReturnSummaryHelper.buildReturnSummaryForVAT(
        rawFiling,
        contributions,
        currency
      );
    }
    return IndirectTaxFilingReturnSummaryHelper.buildReturnSummaryForUS(
      rawFiling,
      currency
    );
  }, [rawFiling, detectedRegion, contributions, canViewDetails]);

  const filing = useMemo<IIndirectTaxFilingDetail | null>(() => {
    if (!rawFiling) return null;

    const mapped = IndirectTaxFilingsHelper.fromApiFilingDetail(
      rawFiling,
      returnSummary
    );

    return {
      ...mapped,
      filingConfirmationDocuments: confirmationDocuments.map(
        IndirectTaxFilingsHelper.toFilingDocument
      ),
      relatedDocuments: [...paymentDocuments, ...returnDocuments].map(
        IndirectTaxFilingsHelper.toFilingDocument
      ),
      remittanceAccount,
    };
  }, [
    rawFiling,
    returnSummary,
    confirmationDocuments,
    paymentDocuments,
    returnDocuments,
    remittanceAccount,
  ]);

  return {
    filing,
    region: detectedRegion,
    canViewDetails,
    returnSummary,
    isLoading: isLoadingFiling || isLoadingContributions,
    error: filingError ?? contributionsError ?? null,
    mutate: () => mutate?.(),
  };
}
